import { FocusMonitor } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { RTL_LOCALE_CODES } from '@rd-lib/rtl-locales';
import { ImpersonationBannerComponent } from '@rd/core/components/impersonation-banner/impersonation-banner.component';
import { RD_PLATFORM_MAINTENANCE_ENABLED } from '@rd/core/tokens/rd-lib.token';
import { DialogContainerReadyService } from '@rd/popup/services/dialog/dialog-container-ready.service';
import { RdMaintenancePagesComponent } from '@rd/shared-legacy/components/maintenance-page/maintenance-page.component';
import { GuardHouseLogoutCodes, Locale } from '@rd/types';
import { Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { LogoutUser } from '../../auth/store/actions/auth.actions';
import { AuthFacade } from '../../auth/store/facades/auth.facade';
import { LocaleFacade } from '../../facades/locale-facade/locale.facade';
import { LoyaltyCurrencyFacade } from '../../facades/loyalty-currency-facade/loyalty-currency.facade';
import { DomService } from '../../services/dom/dom.service';

type LocaleDirection = 'ltr' | 'rtl';
@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    RouterModule,
    ImpersonationBannerComponent,
    RdMaintenancePagesComponent,
  ],
  selector: 'rd-core-main-container',
  templateUrl: './main-container.component.html',
  styleUrls: ['./main-container.component.scss'],
})
export class MainContainerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @HostBinding('class.rd-core-main-container') componentClass = true;

  isRdPlatformMaintenanceEnabled = inject(RD_PLATFORM_MAINTENANCE_ENABLED);

  constructor(
    private store: Store,
    private ngZone: NgZone,
    private loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    private localeFacade: LocaleFacade,
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef,
    private domService: DomService,
    private changeDetectorRef: ChangeDetectorRef,
    private authFacade: AuthFacade,
    private dialogContainerReady: DialogContainerReadyService,
    @Optional() private pointSummaryFacade?: PointsSummaryFacade,
  ) {}

  isLoggedIn$ = this.authFacade.isLoggedIn$;
  authRenderState$ = this.authFacade.authRenderState$;
  loyaltyCurrencySelected$ =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$;
  pointsSummary$ = this.pointSummaryFacade?.pointsSummary$;

  localeDirection$: Observable<LocaleDirection> =
    this.localeFacade.selectedLocale$.pipe(
      map(
        (locale) =>
          (this.isRTLLocale(locale?.code) ? 'rtl' : 'ltr') as LocaleDirection,
      ),
      startWith<LocaleDirection>('ltr'),
    );

  ngOnInit(): void {
    this.localeDirection$
      .pipe(
        tap((localeDirection: LocaleDirection) => {
          this.domService.setDirection(localeDirection);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.focusMonitor.monitor(this.elRef.nativeElement, true).subscribe(() =>
      // We have to do this observable runs outside of the Angular Zone
      // https://material.angular.io/cdk/a11y/overview#focusmonitor
      this.ngZone.run(() => this.changeDetectorRef.markForCheck()),
    );

    this.dialogContainerReady.setReady(true);
  }

  ngOnDestroy(): void {
    this.dialogContainerReady.setReady(false);
  }

  private isRTLLocale(localeCode: Locale['code']): boolean {
    return RTL_LOCALE_CODES.includes(localeCode);
  }

  logout() {
    this.store.dispatch(
      new LogoutUser(GuardHouseLogoutCodes.IntentionalLogout),
    );
  }
}
